/** One account in the Users table, with its Status switch and row actions. */

import { Cloud, KeyRound, Pencil } from 'lucide-react';

import type { UserAccount } from '@/api/types';
import { formatDate, formatDateTime, initialsOf, inCatalogOrder, roleLabel, rolesLabel, sourceLabel } from '@/ui/format';

import type { UserDialogState, UserPerms } from './useUsersTab';

const OWN_ROW_REASON = 'You cannot change your own roles or deactivate your own account.';

const ADMIN_ROW_REASON = 'Only an Admin can change an Admin account.';

const STATUS_TEXT = { true: 'Active', false: 'Deactivated' } as const;
const STATUS_TOOLTIP = {
  true: 'Active. Switch off to deactivate.',
  false: 'Deactivated. Switch on to reactivate.',
} as const;
const SWITCH_VERB = { true: 'Deactivate', false: 'Reactivate' } as const;

interface RowProps {
  u: UserAccount;
  isCaller: boolean;
  perms: UserPerms;
  pending: boolean;
  onToggleActive: (u: UserAccount, active: boolean) => void;
  onDialog: (dialog: UserDialogState) => void;
}

/** A non-Admin caller's actions on an Admin account are refused by the API: disable them up front. */
function lockedToCaller(u: UserAccount, perms: UserPerms): boolean {
  return u.roles.includes('admin') && !perms.callerIsAdmin;
}

function StatusCell({ u, isCaller, perms, pending, onToggleActive }: Omit<RowProps, 'onDialog'>) {
  const locked = lockedToCaller(u, perms);
  const active = String(u.is_active) as 'true' | 'false';
  if (!perms.canStatus) return <td>{STATUS_TEXT[active]}</td>;
  return (
    <td>
      <label className="switch" data-tooltip={locked ? ADMIN_ROW_REASON : isCaller ? OWN_ROW_REASON : STATUS_TOOLTIP[active]}>
        <input
          type="checkbox"
          checked={u.is_active}
          disabled={isCaller || pending || locked}
          aria-label={`${SWITCH_VERB[active]} ${u.display_name}`}
          onChange={(e) => onToggleActive(u, e.target.checked)}
        />
        <span className="switch-track" aria-hidden="true" />
      </label>
    </td>
  );
}

function ActionsCell({ u, perms, onDialog }: Pick<RowProps, 'u' | 'perms' | 'onDialog'>) {
  return (
    <td>
      <div className="row-actions">
        {/* An Entra ID account's password lives with Microsoft: nothing to reset here. */}
        {perms.canReset && u.auth_source === 'local' && (
          <button
            type="button"
            className="btn btn-secondary btn-icon"
            data-tooltip={lockedToCaller(u, perms) ? ADMIN_ROW_REASON : 'Reset password'}
            aria-label={`Reset the password of ${u.display_name}`}
            disabled={lockedToCaller(u, perms)}
            onClick={() => onDialog({ kind: 'password', user: u })}
          >
            <KeyRound aria-hidden="true" />
          </button>
        )}
        {/* Edit is always the last action. */}
        {perms.canEdit && (
          <button
            type="button"
            className="btn btn-secondary btn-icon"
            data-tooltip="Edit name, source and roles"
            aria-label={`Edit ${u.display_name}`}
            onClick={() => onDialog({ kind: 'edit', user: u })}
          >
            <Pencil aria-hidden="true" />
          </button>
        )}
      </div>
    </td>
  );
}

function SourceBadge({ u }: { u: UserAccount }) {
  const entra = u.auth_source === 'entra';
  return (
    <span className={`badge ${entra ? 'badge-info' : 'badge-neutral'}`}>
      {entra ? <Cloud aria-hidden="true" /> : <KeyRound aria-hidden="true" />} {sourceLabel(u.auth_source)}
    </span>
  );
}

/** One badge per role; an account without any says so. */
function RoleBadges({ u }: { u: UserAccount }) {
  if (u.roles.length === 0) return <span className="hint">{rolesLabel([])}</span>;
  return (
    <span className="row role-badges">
      {inCatalogOrder(u.roles).map((role) => (
        <span key={role} className={`badge ${role === 'admin' ? 'badge-accent' : 'badge-info'}`}>{roleLabel(role)}</span>
      ))}
    </span>
  );
}

export function UserRow(props: RowProps) {
  const { u, isCaller, perms } = props;
  const signedIn = formatDateTime(u.last_login_at);
  return (
    <tr>
      <td>
        <span className="row users-identity">
          <span className={`avatar sm${u.is_active ? '' : ' muted'}`} aria-hidden="true">
            {initialsOf(u.display_name)}
          </span>
          <b>{u.display_name}</b>
          {isCaller && <span className="badge badge-neutral">You</span>}
        </span>
      </td>
      <td className="hint">
        {u.email}
        {u.upn && u.upn !== u.email && <div className="sub">{u.upn}</div>}
      </td>
      <td>
        <SourceBadge u={u} />
      </td>
      <td>
        <RoleBadges u={u} />
      </td>
      <StatusCell {...props} />
      <td>{formatDate(u.created_at)}</td>
      <td className={signedIn ? undefined : 'hint'}>{signedIn ?? 'never'}</td>
      {perms.withActions && <ActionsCell {...props} />}
    </tr>
  );
}
